import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import i18n, { SUPPORTED_LANGUAGES } from "@/i18n";

function Logo() {
    return (
        <Link
            to="/"
            className="flex items-center gap-2"
            aria-label="Auronixra home"
            style={{ textDecoration: "none" }}
            data-testid="header-logo"
        >
            <span
                aria-hidden
                style={{
                    width: 34,
                    height: 34,
                    borderRadius: 10,
                    background: "var(--ink)",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--gold)",
                    fontFamily: "Fraunces, serif",
                    fontSize: 20,
                    fontWeight: 600,
                }}
            >
                A
            </span>
            <span
                style={{
                    fontFamily: "Fraunces, serif",
                    fontWeight: 500,
                    fontSize: 22,
                    color: "var(--ink)",
                    letterSpacing: "-0.01em",
                }}
            >
                Auronixra
            </span>
        </Link>
    );
}

export default function Header() {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { lang: paramLang } = useParams();
    const currentLang = SUPPORTED_LANGUAGES.includes(paramLang)
        ? paramLang
        : i18n.language;
    const [open, setOpen] = React.useState(false);

    const switchLanguage = React.useCallback(
        (next) => {
            if (next === currentLang) return;
            // Preserve remainder of the path after language
            const parts = location.pathname.split("/").filter(Boolean);
            if (parts.length && SUPPORTED_LANGUAGES.includes(parts[0])) {
                parts[0] = next;
            } else {
                parts.unshift(next);
            }
            const path = "/" + parts.join("/") + (location.hash || "");
            i18n.changeLanguage(next);
            navigate(path, { replace: true });
        },
        [currentLang, location, navigate],
    );

    const navHome = `/${currentLang}`;
    const links = [
        { to: `${navHome}#how`, label: t("nav.how"), testId: "nav-how" },
        { to: `${navHome}#projects`, label: t("nav.projects"), testId: "nav-projects" },
        { to: `${navHome}#trust`, label: t("nav.trust"), testId: "nav-trust" },
        { to: `${navHome}#contact`, label: t("nav.contact"), testId: "nav-contact" },
    ];

    React.useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header className="aurx-header">
            <div className="aurx-container">
                <div className="aurx-nav">
                    <Logo />

                    <nav
                        className="aurx-nav-links desktop"
                        aria-label="Primary"
                    >
                        {links.map((l) => (
                            <a
                                key={l.to}
                                href={l.to}
                                className="aurx-nav-link"
                                data-testid={l.testId}
                            >
                                {l.label}
                            </a>
                        ))}
                    </nav>

                    <div className="aurx-nav-right">
                        <div
                            className="aurx-lang desktop"
                            role="group"
                            aria-label={t("nav.language")}
                        >
                            {SUPPORTED_LANGUAGES.map((lng) => (
                                <button
                                    key={lng}
                                    onClick={() => switchLanguage(lng)}
                                    className={
                                        currentLang === lng ? "active" : ""
                                    }
                                    aria-pressed={currentLang === lng}
                                    data-testid={`lang-${lng}`}
                                >
                                    {lng.toUpperCase()}
                                </button>
                            ))}
                        </div>
                        <a
                            href={`${navHome}#contact`}
                            className="aurx-btn aurx-btn-primary"
                            style={{ padding: "10px 18px", fontSize: 14 }}
                            data-testid="header-cta"
                        >
                            {t("cta.contact")}
                        </a>
                        <button
                            className="aurx-hamburger"
                            aria-label={
                                open ? t("nav.close_menu") : t("nav.open_menu")
                            }
                            aria-expanded={open}
                            onClick={() => setOpen((v) => !v)}
                            data-testid="hamburger"
                        >
                            {open ? (
                                <X size={20} color="var(--ink)" />
                            ) : (
                                <Menu size={20} color="var(--ink)" />
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {open && (
                <div className="aurx-mobile-panel" data-testid="mobile-menu">
                    {links.map((l) => (
                        <a
                            key={l.to}
                            href={l.to}
                            onClick={() => setOpen(false)}
                        >
                            {l.label}
                        </a>
                    ))}
                    <div
                        className="aurx-lang"
                        style={{ marginTop: 16, width: "fit-content" }}
                    >
                        {SUPPORTED_LANGUAGES.map((lng) => (
                            <button
                                key={lng}
                                onClick={() => switchLanguage(lng)}
                                className={currentLang === lng ? "active" : ""}
                                data-testid={`mobile-lang-${lng}`}
                            >
                                {lng.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
